import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Article as ArticleIcon,
  LocalFireDepartment as FireIcon,
  MenuBook as MenuBookIcon,
  RateReview as RateReviewIcon,
  School as SchoolIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { resendVerificationEmail } from '../api/authApi';
import { fetchReviewQueue, fetchStats, fetchTracks } from '../api/curriculumApi';
import GlossaryTerm from '../components/common/GlossaryTerm';
import { LEARNING_PATHS as ALL_LEARNING_PATHS } from '../constants/learningPaths';

// Paths with a live track/route, sourced from the single learning-paths
// source of truth (src/constants/learningPaths.js) instead of a local copy.
const AVAILABLE_PATHS = ALL_LEARNING_PATHS
  .filter((path) => path.route)
  .map((path) => ({
    slug: path.id,
    label: path.jobReadyLabel,
    description: path.description,
    emoji: path.emoji,
    route: path.route,
  }));

const WAITING_PREVIEWS = [
  {
    eyebrow: 'Step 1',
    title: 'Start with guided daily practice',
    body: 'Your path begins with focused lessons and tasks. Each session gives you a small piece of work to complete and record.',
    story: 'Learn the concept',
    icon: MenuBookIcon,
    preview: 'lesson',
  },
  {
    eyebrow: 'Step 2',
    title: 'Turn practice into portfolio evidence',
    body: 'Your outputs move into your repo as journal notes, screenshots, SQL files, dashboards, scripts, and README summaries.',
    story: 'Save the evidence',
    icon: ArticleIcon,
    preview: 'portfolio',
  },
  {
    eyebrow: 'Step 3',
    title: 'Use hackathons to practise collaboration',
    body: (
      <>
        In the cohort repo, you work through role-based tasks with{' '}
        <GlossaryTerm term="branch">branches</GlossaryTerm>,{' '}
        <GlossaryTerm term="pull request">pull requests</GlossaryTerm>, reviews, and shared delivery habits.
      </>
    ),
    story: 'Collaborate safely',
    icon: AccountTreeIcon,
    preview: 'cohort',
  },
  {
    eyebrow: 'Step 4',
    title: 'Use feedback to sharpen your story',
    body: (
      <>
        Reviews, mock interview prompts, and <GlossaryTerm term="journal coach">Journal Coach</GlossaryTerm> help
        you explain your decisions, tradeoffs, and business impact.
      </>
    ),
    story: 'Explain your value',
    icon: RateReviewIcon,
    preview: 'feedback',
  },
];

// ── Stats banner ──────────────────────────────────────────────────────────────

const StatBox = ({ icon, value, label, color }) => {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={0.5}
      sx={{ flex: 1, minWidth: 100 }}
    >
      <Box sx={{ color: color || 'primary.main', display: 'flex', mb: 0.25 }}>{icon}</Box>
      <Typography variant="h4" fontWeight={800} lineHeight={1}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" textAlign="center">
        {label}
      </Typography>
    </Stack>
  );
};

const StatsBanner = ({ stats, loading }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          {[1, 2, 3].map((i) => (
            <Stack key={i} alignItems="center" spacing={0.5} flex={1}>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="text" width={40} height={40} />
              <Skeleton variant="text" width={80} />
            </Stack>
          ))}
        </Stack>
      </Paper>
    );
  }

  if (!stats) return null;

  const overallProgress =
    stats.total_lessons > 0
      ? Math.round((stats.completed_lessons / stats.total_lessons) * 100)
      : 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)}, ${alpha(theme.palette.secondary.main, 0.04)})`,
        borderColor: alpha(theme.palette.primary.main, 0.2),
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 0 }}
          divider={
            <Box
              sx={{
                width: { xs: '100%', sm: 1 },
                height: { xs: 1, sm: 'auto' },
                bgcolor: 'divider',
                alignSelf: 'stretch',
              }}
            />
          }
        >
          <StatBox
            icon={<TaskAltIcon fontSize="small" />}
            value={stats.completed_lessons}
            label="Lessons complete"
            color={theme.palette.primary.main}
          />
          <StatBox
            icon={<SchoolIcon fontSize="small" />}
            value={stats.total_lessons}
            label="Total lessons"
            color={theme.palette.text.secondary}
          />
          <StatBox
            icon={<FireIcon fontSize="small" />}
            value={stats.streak_days}
            label={`day${stats.streak_days !== 1 ? 's' : ''} streak`}
            color={stats.streak_days > 0 ? theme.palette.warning.main : theme.palette.text.secondary}
          />
        </Stack>

        <Box>
          <Stack direction="row" justifyContent="space-between" mb={0.75}>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Overall progress across all your tracks
            </Typography>
            <Typography variant="caption" fontWeight={700} color="primary.main">
              {overallProgress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '& .MuiLinearProgress-bar': { borderRadius: 4 },
            }}
          />
        </Box>

        {stats.streak_days >= 3 && (
          <Typography variant="caption" color="warning.main" fontWeight={600} textAlign="center">
            {stats.streak_days >= 7
              ? `You are on fire! ${stats.streak_days} days in a row.`
              : `${stats.streak_days} days in a row — keep it going!`}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

// ── Waiting playground ───────────────────────────────────────────────────────

const PreviewSnapshot = ({ type }) => {
  const theme = useTheme();

  if (type === 'portfolio') {
    return (
      <Stack spacing={1.25}>
        {['journal.md', 'dashboards/week-02-summary.xlsx', 'README evidence bullets'].map((item, index) => (
          <Stack
            key={item}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              p: 1.25,
              borderRadius: 1,
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.18),
              bgcolor: index === 0 ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
            }}
          >
            <ArticleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" fontWeight={800} noWrap>{item}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Saved evidence from your work
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    );
  }

  if (type === 'cohort') {
    return (
      <Stack spacing={1.25}>
        {[
          ['Role', 'Quality Editor'],
          ['Branch', 'john-week-02'],
          ['Pull request', 'Ready for review'],
        ].map(([label, value]) => (
          <Stack
            key={label}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 1.25, borderRadius: 1, bgcolor: alpha(theme.palette.info.main, 0.08) }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography>
            <Chip label={value} size="small" color={label === 'Pull request' ? 'success' : 'primary'} variant="outlined" />
          </Stack>
        ))}
      </Stack>
    );
  }

  if (type === 'feedback') {
    return (
      <Stack spacing={1.25}>
        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.success.main, 0.08) }}>
          <Typography variant="caption" fontWeight={900} color="success.main">Review note</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Explain the business impact before the technical steps.
          </Typography>
        </Box>
        <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={900}>Interview prompt</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Tell me about a messy dataset you cleaned.
          </Typography>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.25}>
      <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
        <Typography variant="caption" fontWeight={900} color="primary.main">Current lesson</Typography>
        <Typography variant="body2" fontWeight={800} sx={{ mt: 0.5 }}>
          Clean a messy Monday export
        </Typography>
        <LinearProgress variant="determinate" value={45} sx={{ mt: 1.25, height: 7, borderRadius: 4 }} />
      </Box>
      {['Inspect the raw data', 'Fix obvious quality issues', 'Write a short journal note'].map((task, index) => (
        <Stack key={task} direction="row" spacing={1} alignItems="center">
          <TaskAltIcon sx={{ fontSize: 18, color: index === 0 ? 'success.main' : 'text.disabled' }} />
          <Typography variant="caption" color={index === 0 ? 'text.primary' : 'text.secondary'}>{task}</Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const WaitingPlayground = () => {
  const theme = useTheme();
  const [active, setActive] = useState(0);
  const item = WAITING_PREVIEWS[active];
  const Icon = item.icon;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % WAITING_PREVIEWS.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, []);

  const move = (direction) => {
    setActive((current) => (current + direction + WAITING_PREVIEWS.length) % WAITING_PREVIEWS.length);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 3 },
        borderColor: alpha(theme.palette.primary.main, 0.18),
        bgcolor: alpha(theme.palette.primary.main, 0.025),
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" fontWeight={900}>
            Your learning path is being prepared
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            While your cohort and lessons are being assigned, this is the program flow: learn the concept,
            save evidence, collaborate safely, then explain your work with confidence.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' },
            gap: 1,
          }}
        >
          {WAITING_PREVIEWS.map((preview, index) => (
            <Box
              key={preview.story}
              onClick={() => setActive(index)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setActive(index);
              }}
              sx={{
                p: 1,
                borderRadius: 1,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: index === active ? 'primary.main' : 'divider',
                bgcolor: index === active ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
              }}
            >
              <Typography variant="caption" color={index === active ? 'primary.main' : 'text.secondary'} fontWeight={900}>
                {preview.eyebrow}
              </Typography>
              <Typography variant="caption" display="block" fontWeight={800}>
                {preview.story}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.85fr 1.15fr' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="caption" color="primary.main" fontWeight={900} textTransform="uppercase">
                    {item.eyebrow}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={900}>
                    {item.title}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {item.body}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" variant="outlined" onClick={() => move(-1)} startIcon={<ArrowBackIcon />}>
                  Previous
                </Button>
                <Button size="small" variant="contained" onClick={() => move(1)} endIcon={<ArrowForwardIcon />}>
                  Next
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <PreviewSnapshot type={item.preview} />
          </Box>
        </Box>

        <Stack direction="row" spacing={0.75} justifyContent="center" aria-label="Preview slides">
          {WAITING_PREVIEWS.map((preview, index) => (
            <Box
              key={preview.title}
              component="button"
              type="button"
              aria-label={`Show ${preview.eyebrow}`}
              onClick={() => setActive(index)}
              sx={{
                width: index === active ? 22 : 8,
                height: 8,
                border: 0,
                borderRadius: 99,
                cursor: 'pointer',
                bgcolor: index === active ? 'primary.main' : alpha(theme.palette.primary.main, 0.25),
                transition: 'width 0.2s ease',
              }}
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};

// ── Track card ────────────────────────────────────────────────────────────────

const TrackCard = ({ track }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const totalLessons = track.courses.reduce((sum, c) => sum + c.lesson_count, 0);
  const completedLessons = track.courses.reduce((sum, c) => sum + c.completed_lesson_count, 0);
  const progress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: alpha(track.color || theme.palette.primary.main, 0.4),
        '&:hover': {
          borderColor: track.color || theme.palette.primary.main,
          boxShadow: theme.shadows[4],
        },
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <CardActionArea
        onClick={() => navigate(`/roadmaps/${track.slug}`)}
        sx={{ p: 0 }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(track.color || theme.palette.primary.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.6rem',
                  flexShrink: 0,
                }}
              >
                {track.icon}
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                  {track.name}
                </Typography>
                <Chip
                  label={`${track.courses.length} course${track.courses.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {track.description}
            </Typography>

            <Box>
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {completedLessons} of {totalLessons} lessons complete
                </Typography>
                <Typography variant="caption" fontWeight={600} color={track.color || 'primary.main'}>
                  {progress}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: alpha(track.color || theme.palette.primary.main, 0.12),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: track.color || theme.palette.primary.main,
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            {track.courses.length > 0 && (
              <Stack spacing={0.75}>
                {track.courses.map((course) => (
                  <Stack
                    key={course.slug}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/roadmaps/${track.slug}`);
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {course.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {course.completed_lesson_count}/{course.lesson_count} lessons
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Chip label={course.level} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                      <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const TrackCardSkeleton = () => (
  <Card variant="outlined">
    <CardContent sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5}>
          <Skeleton variant="rounded" width={48} height={48} />
          <Box flex={1}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="30%" />
          </Box>
        </Stack>
        <Skeleton variant="text" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="rounded" height={6} />
        <Skeleton variant="rounded" height={48} />
      </Stack>
    </CardContent>
  </Card>
);

const LinkedPathCard = ({ path }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: alpha(theme.palette.primary.main, 0.25),
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: theme.shadows[4],
        },
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <CardActionArea onClick={() => navigate(path.route)}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.6rem',
                  flexShrink: 0,
                }}
              >
                {path.emoji}
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                  {path.label}
                </Typography>
                <Chip label="Track" size="small" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
              </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {path.description}
            </Typography>
            <Button variant="outlined" endIcon={<ArrowForwardIcon />} sx={{ alignSelf: 'flex-start' }}>
              Open track
            </Button>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const ReviewQueue = ({ items }) => {
  const navigate = useNavigate();

  if (!items?.length) return null;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Review queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lessons worth revisiting — either you haven't finished the practice challenge yet, or you rated
            your own confidence low last time.
          </Typography>
        </Box>
        <Stack spacing={1}>
          {items.slice(0, 5).map((item) => (
            <Stack
              key={item.lesson.slug}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              gap={1}
              sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {item.lesson.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <GlossaryTerm term="challenge status">{item.challenge_status.replaceAll('_', ' ')}</GlossaryTerm>
                  {' · '}
                  <GlossaryTerm term="confidence">confidence {item.confidence || 0}%</GlossaryTerm>
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const dest = item.lesson.track_slug
                    ? `/roadmaps/${item.lesson.track_slug}?lesson=${item.lesson.slug}`
                    : `/my-learning`;
                  navigate(dest);
                }}
              >
                Review
              </Button>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const MyLearningPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [stats, setStats] = useState(null);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState('');

  const nextUpTrack =
    tracks.find((track) =>
      track.courses.some((course) => course.completed_lesson_count < course.lesson_count)
    ) || tracks[0];
  const nextUpCourse =
    nextUpTrack?.courses.find((course) => course.completed_lesson_count < course.lesson_count) ||
    nextUpTrack?.courses[0];
  const publishedTrackPathKeys = new Set(
    tracks.flatMap((track) => [track.slug, track.learning_path_key]).filter(Boolean)
  );
  const publishedTrackRoutes = new Set(tracks.map((track) => `/roadmaps/${track.slug}`));

  const loadLearning = useCallback(() => {
    setLoading(true);
    Promise.all([fetchTracks(), fetchStats().catch(() => null), fetchReviewQueue(10).catch(() => [])])
      .then(([tracksData, statsData, reviewData]) => {
        setTracks(tracksData);
        setStats(statsData);
        setReviewQueue(reviewData);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const resendVerification = async () => {
    setVerificationStatus('Sending verification email...');
    try {
      await resendVerificationEmail();
      setVerificationStatus('Verification email sent. Check your inbox.');
    } catch (err) {
      setVerificationStatus(err.message);
    }
  };

  useEffect(() => {
    loadLearning();
  }, [loadLearning]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={4}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5} flexWrap="wrap">
            <SchoolIcon color="primary" />
            <Typography variant="h4" fontWeight={800}>
              My Learning
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary">
            Welcome back{user ? `, ${user.first_name}` : ''}. Pick up where you left off.
          </Typography>
        </Box>

        {!loading && nextUpCourse && (
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate(`/roadmaps/${nextUpTrack.slug}`)}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, textAlign: 'left' }}
          >
            Continue learning: {nextUpCourse.name}
          </Button>
        )}

        <StatsBanner stats={stats} loading={loading} />

        {user && !user.email_verified && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={resendVerification}>
                Resend
              </Button>
            }
          >
            Please verify your email address. {verificationStatus}
          </Alert>
        )}

        {!loading && <ReviewQueue items={reviewQueue} />}

        {!loading && !user?.learning_paths?.length && (
          <WaitingPlayground />
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            {[1, 2].map((i) => <TrackCardSkeleton key={i} />)}
          </Box>
        ) : (
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            {tracks.map((track) => (
              <TrackCard key={track.slug} track={track} />
            ))}
            {AVAILABLE_PATHS
              .filter((path) =>
                path.route &&
                user?.learning_paths?.includes(path.slug) &&
                !publishedTrackPathKeys.has(path.slug) &&
                !publishedTrackRoutes.has(path.route)
              )
              .map((path) => (
                <LinkedPathCard key={path.slug} path={path} />
              ))}
          </Box>
        )}
      </Stack>
    </Container>
  );
};

export default MyLearningPage;
